import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useCallback, useEffect, useState } from 'react';
import { IPropsEmployeePage } from '../Employee';
import { Button, DayScheduleEditor, IDaySchedule, weeklySchedulesToDays, scheduleToApiDto, validateScheduleDays } from '../../../shared/components';
import { feedback, finishRequest, businessEmployeeService, IBusinessEmployeeServiceView, IUpdateBusinessEmployee } from '../../../shared/services';
import { useMutation } from '@tanstack/react-query';
import { BusinessEmployeeService } from '../../../shared/components/SconApp/businessEmployeeService/BusinessEmployeeService';

export const EmployeeInformation: React.FC<IPropsEmployeePage> = ({ employee, refetch, business, selectedBizEmp }) => {
  const [schedule, setSchedule] = useState<IDaySchedule[]>(() => []);
  const [services, setServices] = useState<{ [key: number]: IBusinessEmployeeServiceView[] }>({});

  // Usa o businessEmployee selecionado, ou o primeiro como fallback
  const businessEmployee = selectedBizEmp ?? (business
    ? employee.businessEmployees?.find(be => be.businessId === business.id)
    : employee.businessEmployees?.[0]);

  // Load schedule from the selected business employee (guard against null/empty)
  useEffect(() => {
    if (businessEmployee?.weeklySchedules && businessEmployee.weeklySchedules.length > 0) {
      setSchedule(weeklySchedulesToDays(businessEmployee.weeklySchedules));
      setServices(prev => ({
        ...prev,
        [businessEmployee.businessId]: businessEmployee.businessEmployeeServices || [],
      }));
    } else if (businessEmployee) {
      // At least sync services even if schedules are not loaded
      setServices(prev => ({
        ...prev,
        [businessEmployee.businessId]: businessEmployee.businessEmployeeServices || [],
      }));
    }
  }, [businessEmployee]);

  const { mutate: updateBusinessEmployee, isLoading: isLoadingUpdateBusinessEmployee } = useMutation(
    (data: IUpdateBusinessEmployee) =>
      businessEmployeeService.update(data),
    {
      onSuccess: () => finishRequest('Salvo com sucesso!', 'success'),
      onError: (error: Error) => finishRequest(error.message, 'error'),
    }
  );

  const handleSubmit = useCallback(() => {
    if (!businessEmployee) return;

    const err = validateScheduleDays(schedule);
    if (err) { feedback(err, 'error'); return; }

    // ── Clamp employee hours to business hours ──
    let effectiveSchedule = schedule;
    if (business?.weeklySchedules && business.weeklySchedules.length > 0) {
      const businessSchedules = business.weeklySchedules;
      const dayNames = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
      const adjustments: string[] = [];

      const clampedSchedule = schedule.map(day => {
        const clamped = { ...day };
        const bizDay = businessSchedules.find(s => s.dayOfWeek === day.dayOfWeek);
        if (clamped.isClosed || !bizDay || bizDay.isClosed) return clamped;

        if (clamped.startTime && bizDay.startTime && clamped.startTime < bizDay.startTime)
          clamped.startTime = bizDay.startTime;
        if (clamped.endTime && bizDay.endTime && clamped.endTime > bizDay.endTime)
          clamped.endTime = bizDay.endTime;

        return clamped;
      });

      for (let i = 0; i < schedule.length; i++) {
        if (schedule[i].isClosed) continue;
        const bizDay = businessSchedules.find(s => s.dayOfWeek === schedule[i].dayOfWeek);
        if (!bizDay || bizDay.isClosed) continue;
        if (schedule[i].startTime !== clampedSchedule[i].startTime)
          adjustments.push(`${dayNames[schedule[i].dayOfWeek]}: início ajustado de ${schedule[i].startTime} para ${clampedSchedule[i].startTime}`);
        if (schedule[i].endTime !== clampedSchedule[i].endTime)
          adjustments.push(`${dayNames[schedule[i].dayOfWeek]}: término ajustado de ${schedule[i].endTime} para ${clampedSchedule[i].endTime}`);
      }

      if (adjustments.length > 0) {
        feedback('Horários ajustados: ' + adjustments.join('. '), 'info');
        effectiveSchedule = clampedSchedule;
      }
    }

    const weeklySchedules = scheduleToApiDto(effectiveSchedule);

    const submitData: IUpdateBusinessEmployee = {
      id: businessEmployee.id,
      businessId: businessEmployee.businessId,
      employeeId: businessEmployee.employeeId,
      servicesCommissionRate: businessEmployee.servicesCommissionRate,
      color: businessEmployee.color,
      businessEmployeeServices: services[businessEmployee.businessId],
      weeklySchedules,
    };

    updateBusinessEmployee(submitData);
    refetch?.();
  }, [businessEmployee, schedule, services, updateBusinessEmployee, refetch, business?.weeklySchedules]);

  if (!businessEmployee) return null;

  return (
    <Box display='flex' flexDirection='column' gap={2}>
      <Box display='flex' justifyContent='end'>
        <Button label='Salvar' variant='contained' onClick={handleSubmit} isLoading={isLoadingUpdateBusinessEmployee} />
      </Box>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Horários de Trabalho</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {schedule.length > 0 && (
            <DayScheduleEditor
              schedule={schedule}
              onChange={setSchedule}
            />
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography fontWeight={600}>Serviços</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <BusinessEmployeeService
            businessEmployeeId={businessEmployee.id}
            businessId={businessEmployee.businessId}
            services={business?.services || []}
            employeeServices={services}
            setEmployeeServices={setServices}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
